import { CellProps, NodeValues, useFocusManagement } from "components/TreeState"
import { ChangedPlanRecord, PlanRecord, PlanRecordType } from "../types"
import { checkBuyingStatus } from "utils/type-guards"

import Autocomplete from "@material-ui/lab/Autocomplete"
import { Bulky } from "./Bulky"
import { CellError } from "./CellError"
import DropdownIcon from "@material-ui/icons/KeyboardArrowDown"
import { FieldsKey } from "../components/AutoCompute/types"
import { Info } from "./Info"
import TextField from "@material-ui/core/TextField"
import { Tooltip } from "@material-ui/core"
import { css } from "@emotion/css"
import { get } from "lodash"
import { isSemanticReadonly } from "../utils"
import { set } from "lodash/fp"
import { useAutoCompute } from "../components/AutoCompute/useAutoCompute"
import { useAutocomputeValidation } from "../useAutocomputeValidation"
import { useBillingTypesRules } from "../useBillingTypeRules"
import { useDefaultFieldIds } from "../useDefaultFieldIds"
import { useSnapshot } from "valtio"
import { useTableSource } from "../TableStateProvider"
import { useUndoStack } from "../useUndoStack"
import { getMediaField } from "../components/AutoCompute/utils"

type Option = {
  label: string
  value: string
  disabled?: boolean
}

export function ListCell(
  props: CellProps & {
    otherDisabled?: boolean
    options?: Option[]
    onChange?: (value?: string) => void
    bulkDisabled?: boolean
    disableClearable?: boolean
    selected?: string
    secondary?: string
    normalize?: (value: string) => any
    bulkDependencies?: (
      row: NodeValues<ChangedPlanRecord>,
      value?: any | null,
    ) => NodeValues<ChangedPlanRecord>
  },
) {
  const {
    rowId: id,
    dataKey,
    options,
    columnId,
    disabled,
    otherDisabled,
    bulkDisabled,
    disableClearable,
    bulkDependencies,
    category,
    onChange,
    selected,
    normalize,
    primaryKey,
    semantic,
  } = props
  const defaultFieldIds = useDefaultFieldIds(id)
  const tableSource = useTableSource()
  const autoCompute = useAutoCompute()
  const readFrom = useSnapshot(tableSource.state)
  const readError = useSnapshot(tableSource.errors)
  const globalUndoState = useUndoStack<ChangedPlanRecord>()
  const writeTo = tableSource.state

  const values = readFrom[id] || {}
  const { type } = values
  const isMediaSubmedia =
    type === "media_type_plan" || type === "media_subtype_plan"
  const readonly =
    isMediaSubmedia &&
    dataKey !== "mediaId" &&
    !!semantic &&
    !["targetA", "targetB"].includes(semantic) &&
    category !== "SPOTS_PLAN"

  const buyingStatusVal = String(get(readFrom, `${id}.buyingStatus`))
  const buyingStatus = checkBuyingStatus(buyingStatusVal) ? buyingStatusVal : ""

  const isTargetBCell = semantic === "targetB"
  const grpBField = getMediaField("grpB")?.id
  const impressionsBField = getMediaField("impressionsB")?.id
  const cppBField = getMediaField("cppB")?.id
  const cpmBField = getMediaField("cpmB")?.id

  const error = get(readError, `${id}.${columnId}`)
  const value = selected || String(get(readFrom, `${id}.${dataKey}`))

  const action = readFrom[id]?.action
  const packaged = readFrom[id]?.packaged
  const updatable = !action || action === "UPDATE_ORDER"
  const option = options?.find((opt) => opt.value === value)

  const { handleFocus: autoFocus, hasChanged } =
    useAutocomputeValidation(option)

  const handleChange = (
    _event: React.ChangeEvent<{}>,
    selected: { value: string; label: string } | null,
  ) => {
    tableSource.clearError(id)
    globalUndoState.push([writeTo[id] as ChangedPlanRecord])
    const newRaw = selected ? selected.value : null
    const newValue = !!normalize && !!newRaw ? normalize(newRaw) : newRaw
    writeTo[id] = set(dataKey, newValue, writeTo[id])
    if (isTargetBCell) {
      writeTo[id] = set(`${primaryKey}.${grpBField}`, undefined, writeTo[id])
      writeTo[id] = set(
        `${primaryKey}.${impressionsBField}`,
        undefined,
        writeTo[id],
      )
      writeTo[id] = set(`${primaryKey}.${cppBField}`, undefined, writeTo[id])
      writeTo[id] = set(`${primaryKey}.${cpmBField}`, undefined, writeTo[id])
    }
    updatable && (writeTo[id] = set("action", "UPDATE", writeTo[id]))
    onChange && onChange(selected?.value)
    hasChanged(selected) &&
      autoCompute?.compute(writeTo[id] as ChangedPlanRecord)
  }

  const handleFocus = () => {
    ip.onFocus()
    autoFocus()
  }

  const { getInputProps } = useFocusManagement()
  const ip = getInputProps()

  const mediaFields = readFrom[id]?.[
    primaryKey as keyof PlanRecord
  ] as PlanRecord["planningMediaFields"]

  const isReadonlySemantic = isSemanticReadonly(
    type as PlanRecordType,
    primaryKey as FieldsKey,
    semantic,
    packaged,
    defaultFieldIds,
    mediaFields,
  )

  const isDisabled =
    disabled ||
    otherDisabled ||
    readonly ||
    isReadonlySemantic ||
    buyingStatus === "POSTED_FOR_BILLING"

  // apply billing type rules
  const viewable = useBillingTypesRules(
    values as ChangedPlanRecord,
    primaryKey,
    semantic,
  )
  if (!viewable) return <span />

  return (
    <div className={styles.wrapper}>
      <Bulky
        {...props}
        otherDisabled={readonly || otherDisabled || bulkDisabled}
        applyDependencies={bulkDependencies}
      >
        <div style={{ width: "100%" }}>
          <Tooltip title={option?.label || ""} placement="bottom-start">
            <Autocomplete
              disabled={isDisabled}
              classes={{
                popper: styles.popper,
                option: styles.option,
                endAdornment: isDisabled ? styles.hidden : undefined,
                inputRoot: styles.autocompleteInput,
              }}
              getOptionLabel={(opt) => opt.label}
              value={option || null}
              onChange={handleChange}
              options={options || []}
              openOnFocus={false}
              onKeyDown={ip.onKeyDown}
              filterSelectedOptions
              getOptionDisabled={(opt) => !!opt?.disabled}
              getOptionSelected={(opt, val) => opt?.value === val?.value}
              popupIcon={<DropdownIcon className="icon" />}
              className={styles.auto}
              disableClearable={
                !option ||
                disabled ||
                type === "media_subtype_plan" ||
                disableClearable
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  onFocus={handleFocus}
                  onBlur={ip.onBlur}
                  fullWidth
                  name={dataKey}
                  variant="standard"
                  InputProps={{
                    disableUnderline: true,
                    classes: {
                      root: styles.inputRoot,
                      input: styles.input,
                    },
                    ...params.InputProps,
                  }}
                />
              )}
            />
          </Tooltip>
        </div>
      </Bulky>
      <Info {...props} />
      {error ? <CellError error={error} /> : null}
    </div>
  )
}

const styles = {
  wrapper: css`
    width: 100%;
    display: flex;
  `,
  wrapperColumn: css`
    flex-direction: column;
  `,
  auto: css`
    width: inherit;
    display: flex;
    padding: 0;
    .icon {
      opacity: 0;
    }
    :hover {
      .icon {
        opacity: 1;
      }
    }
  `,
  autocompleteInput: css`
    min-height: var(--spacing-m);
  `,
  popper: css`
    width: max-content;
    min-width: max-content;
  `,
  option: css`
    font-size: 13px;
    white-space: nowrap;
  `,
  inputRoot: css`
    font-size: 13px;
    font-weight: inherit;
    padding: 0;
    align-items: center;
    &.Mui-disabled {
      color: inherit;
      padding: 0;
    }
    color: inherit !important;
  `,
  input: css`
    padding: 0 !important;
    font-size: 13px;
    font-weight: inherit;
  `,
  hidden: css`
    display: none;
  `,
}
